import { Component, ViewChild } from '@angular/core'
import { ReadyToPayListComponent } from './ready-to-pay-list/ready-to-pay-list.component'
import { PendingPaymentListComponent } from './pending-payment-list/pending-payment-list.component'
import {
  IPaymentDetailsModel,
  PaymentDetailsComponent
} from './payment-details/payment-details.component'
import {
  IElectronicFundsPayment,
  IElectronicFundsPaymentDetails,
  IPayment,
  IPaymentDetails,
  IReadyForPayment,
  PaymentService,
  PaymentType,
  PaymentODataService
} from '@exchange/domain/.'
import { GridQuickFilterServiceDirective, IFilterOption, IQuickFilter } from '@exchange/domain/.'
import { tap } from 'rxjs/operators'
import { ToastrService } from 'ngx-toastr'

@Component({
  selector: 'app-payments',
  templateUrl: './payments.component.html',
  standalone: false
})
export class PaymentsComponent {
  @ViewChild('readyToPay', { static: true }) readyToPay: ReadyToPayListComponent
  @ViewChild('pendingPayment', { static: true }) pendingPayment: PendingPaymentListComponent
  @ViewChild('paymentDetails', { static: true }) paymentDetails: PaymentDetailsComponent

  quickFilters: IFilterOption[] = [
    {
      displayName: 'Provider Name',
      fieldName: 'Name',
      type: 'string',
      operation: 'contains',
      default: true
    },
    { displayName: 'Provider NPI', fieldName: 'Npi', type: 'number', operation: 'contains' },
    { displayName: 'CHI Claim #', fieldName: 'ClaimId', type: 'number', operation: 'contains' }
  ]

  constructor(
    private paymentService: PaymentService,
    private gridQuickFilterServiceDirective: GridQuickFilterServiceDirective,
    private toastr: ToastrService,
    private paymentODataService: PaymentODataService
  ) {
    this.gridQuickFilterServiceDirective.search.subscribe(this.quickFilter.bind(this))
    this.gridQuickFilterServiceDirective.resetFilters.subscribe(this.quickFilterReset.bind(this))
  }

  generatePayment(payments: IReadyForPayment[]) {
    const amount = payments.map(payment => payment.PayAmount).reduce((prev, next) => prev + next)

    this.paymentDetails.collectPaymentDetails(payments, amount).subscribe(() => {
      this.paymentDetails.observable
        .pipe(
          tap(paymentDetailsModel => {
            const payment = this.convertPaymentModelToPayment(paymentDetailsModel)

            this.paymentODataService
              .query()
              .filter(`Memo eq '${payment.Details.Memo}'`)
              .exec()
              .subscribe(response => {
                if (response.data.length) {
                  this.toastr.error(
                    `Memo: '${payment.Details.Memo}' has been used, please choose a unique memo.`
                  )
                } else {
                  switch (paymentDetailsModel.paymentType) {
                    case PaymentType.CHK:
                      return this.makeCheckPayment(payment as IPayment)
                    case PaymentType.EFT:
                      return this.makeEftPayment(payment as IElectronicFundsPayment)
                    case PaymentType.INFO:
                      return this.makeInformationalPayment(payment as IPayment)
                  }
                }
              })
          })
        )
        .subscribe(() => this.refreshGrids())
    })
  }

  quickFilter(item: IQuickFilter) {
    this.readyToPay.filterGrid(item)
    this.pendingPayment.filterGrid(item)
  }

  quickFilterReset() {
    this.readyToPay.resetGridFilter()
    this.pendingPayment.resetGridFilter()
  }

  refreshGrids() {
    this.readyToPay.refresh()
    this.pendingPayment.refresh()
  }

  private makeCheckPayment(payment: IPayment) {
    this.paymentService.makeCheckPayment(payment).subscribe()
  }

  private makeEftPayment(payment: IElectronicFundsPayment) {
    this.paymentService.makeETFPayment(payment).subscribe()
  }

  private makeInformationalPayment(payment: IPayment) {
    this.paymentService.makeInformationalPayment(payment).subscribe()
  }

  private convertPaymentModelToPayment(paymentDetailsModel: IPaymentDetailsModel) {
    if (paymentDetailsModel.paymentType === PaymentType.EFT) {
      return {
        Id: null,
        Version: null,
        ServiceLines: paymentDetailsModel.readyForPayments,
        Details: {
          PaymentDate: paymentDetailsModel.paymentDate,
          Amount: paymentDetailsModel.totalPayment,
          Memo: paymentDetailsModel.memo,
          SenderIdentifier: paymentDetailsModel.payerAccountNumberQualifier,
          SenderName: paymentDetailsModel.payerName,
          SenderAccountNumber: paymentDetailsModel.payerAccountNumber,
          SenderRoutingNumber: paymentDetailsModel.payerRoutingNumber,
          ReceiverIdentifier: paymentDetailsModel.payeeAccountNumberQualifier,
          ReceiverName: paymentDetailsModel.payeeName,
          ReceiverAccountNumber: paymentDetailsModel.payeeAccountNumber,
          ReceiverRoutingNumber: paymentDetailsModel.payeeRoutingNumber
        } as IElectronicFundsPaymentDetails
      } as IElectronicFundsPayment
    } else {
      return {
        Id: null,
        Version: null,
        ServiceLines: paymentDetailsModel.readyForPayments,
        Details: {
          PaymentDate: paymentDetailsModel.paymentDate,
          Amount: paymentDetailsModel.totalPayment,
          Memo: paymentDetailsModel.memo,
          SenderName: paymentDetailsModel.payerName
        } as IPaymentDetails
      } as IPayment
    }
  }
}
